import { type FC, type ReactNode } from 'react';
import {
    vettedSeller,
    goldSeller,
    platinumSeller,
    diamondSeller,
    recognizedTitle,
    recognizedSeller,
    recognizedDescription,
    goldTooltip,
    platinumTooltip,
    diamondTooltip,
    vettedTooltip,
} from '../messages/pdpDistinctionsMessages.jsx';
import { graphql, createFragmentContainer } from 'react-relay/legacy.js';

// components
import Recognition from 'dibs-icons/exports/Recognition.ts';
import Diamond from 'dibs-icons/exports/Diamond.ts';
import Medal from 'dibs-icons/exports/Medal.ts';
import Vetting from 'dibs-icons/exports/Vetting.ts';

// styles
import classnames from 'classnames';
import styles from './PdpSharedDealerDistinction.scss';
import dibsCss from 'dibs-css';

// types
import { type PdpSharedDealerDistinction_seller$data as Seller } from './__generated__/PdpSharedDealerDistinction_seller.graphql.ts';

type Props = {
    seller: Seller;
    hideDescription?: boolean;
    useDistinguishedShortTitle?: boolean;
};

type CreateDealerDistinctionProps = {
    icon: ReactNode;
    title: ReactNode;
    description: ReactNode;
    hideDescription: boolean;
};

const createDealerDistinction = ({
    icon,
    title,
    description,
    hideDescription,
}: CreateDealerDistinctionProps): ReactNode => (
    <>
        <div className={classnames(styles.distinction, dibsCss.flex, dibsCss.itemsCenter)}>
            {icon}
            <span className={styles.title}>{title}</span>
        </div>
        {!hideDescription && (
            <div className={classnames(styles.description, dibsCss.textTrademember)}>
                {description}
            </div>
        )}
    </>
);

const PdpSharedDealerDistinctionComponent: FC<Props> = ({
    seller,
    hideDescription = false,
    useDistinguishedShortTitle = false,
}) => {
    const sellerTiers = seller?.sellerTiers;
    const isRewardsTier2 = seller?.isRewardsTier2;
    const isDistinguished = seller?.isDistinguished;
    const sellerTierDataFound = sellerTiers?.sellerTierDataNotFound;
    const isGoldSeller = !sellerTierDataFound && sellerTiers?.isGoldTier;
    const isPlatinumSeller = !sellerTierDataFound && sellerTiers?.isPlatinumTier;
    const distinctions = [];

    if (isDistinguished) {
        distinctions.push(
            createDealerDistinction({
                icon: (
                    <span className={dibsCss.fillSuccesssecondary}>
                        <Recognition variation="20" />
                    </span>
                ),
                title: useDistinguishedShortTitle ? recognizedTitle : recognizedSeller,
                description: recognizedDescription,
                hideDescription,
            })
        );
    }

    if (isGoldSeller && !isRewardsTier2) {
        distinctions.push(
            createDealerDistinction({
                icon: (
                    <span
                        className={classnames(
                            dibsCss.shrink0,
                            dibsCss.grow0,
                            dibsCss.mrXsmall,
                            dibsCss.fillBuyersecondary
                        )}
                    >
                        <Medal variation="20" />
                    </span>
                ),
                title: goldSeller,
                description: goldTooltip,
                hideDescription,
            })
        );
    } else if (isPlatinumSeller && !isRewardsTier2) {
        distinctions.push(
            createDealerDistinction({
                icon: (
                    <span
                        className={classnames(
                            dibsCss.shrink0,
                            dibsCss.grow0,
                            dibsCss.mrXsmall,
                            dibsCss.fillMedalplatinum
                        )}
                    >
                        <Medal variation="20" />
                    </span>
                ),
                title: platinumSeller,
                description: platinumTooltip,
                hideDescription,
            })
        );
    } else if (isRewardsTier2) {
        distinctions.push(
            createDealerDistinction({
                icon: (
                    <span className={classnames(dibsCss.shrink0, dibsCss.grow0, dibsCss.mrXsmall)}>
                        <Diamond variation="20" />
                    </span>
                ),
                title: diamondSeller,
                description: diamondTooltip,
                hideDescription,
            })
        );
    } else if (!distinctions.length) {
        distinctions.push(
            createDealerDistinction({
                icon: (
                    <span
                        className={classnames(
                            dibsCss.shrink0,
                            dibsCss.grow0,
                            dibsCss.mrXsmall,
                            dibsCss.fillShagreen800
                        )}
                    >
                        <Vetting variation="20" />
                    </span>
                ),
                title: vettedSeller,
                description: vettedTooltip,
                hideDescription,
            })
        );
    }

    return (
        <>
            {distinctions.map((distinction, index) => (
                <div key={`distinction-${index}`}>{distinction}</div>
            ))}
        </>
    );
};

export const PdpSharedDealerDistinction = createFragmentContainer(
    PdpSharedDealerDistinctionComponent,
    {
        seller: graphql`
            fragment PdpSharedDealerDistinction_seller on Seller {
                isDistinguished
                isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
                sellerTiers {
                    isGoldTier
                    isPlatinumTier
                    sellerTierDataNotFound
                }
            }
        `,
    }
);
